import alwaysDeleteConversationsService from '../services/alwaysDeleteConversationsService';
import createSweepRuleForSenderService from '../services/createSweepRuleForSenderService';
import { TableView } from 'owa-mail-list-store';
import {
    setSweepRequestStatus,
    SweepRequestStatus,
    SweepOptionType,
    SweepBulkActionCustomData,
} from 'owa-mail-sweep-store';
import EmailAddressWrapper from 'owa-service/lib/contract/EmailAddressWrapper';
import KeepNType from 'owa-service/lib/contract/KeepNType';
import { action } from 'satcheljs/lib/legacy';
import { isFeatureEnabled } from 'owa-feature-flags';
import { lazyOnBulkActionSubmitted } from 'owa-bulk-action-store';
import folderNameToId from 'owa-session-store/lib/utils/folderNameToId';

export interface KeepNValueType {
    keepNType: KeepNType;
    keepNCount: number;
}

export interface KeepNParametersTable {
    [id: number]: KeepNValueType;
}

/**
 * The keepNParametersTable is used to hold the keepN parameters that used for
 * ApplyBulkItemAction and CreateSweepRuleForSenderAction
 */
const keepNParametersTable: KeepNParametersTable = {};
keepNParametersTable[SweepOptionType.All] = { keepNType: KeepNType.Items, keepNCount: 0 };
keepNParametersTable[SweepOptionType.AllCurrentAndFuture] = {
    keepNType: KeepNType.Items,
    keepNCount: 0,
};
keepNParametersTable[SweepOptionType.AllButOne] = { keepNType: KeepNType.Items, keepNCount: 1 };
keepNParametersTable[SweepOptionType.AllOlderThan] = { keepNType: KeepNType.Days, keepNCount: 10 };

/**
 * Get the keep n parameters from the table
 * @param sweepOptionType the sweep option type
 */
function getKeepNParameters(sweepOptionType: SweepOptionType) {
    return keepNParametersTable[sweepOptionType];
}

/**
 * Perform the sweep conversations action
 * @param sweepableMailboxes the sweepable mailboxes (ie not your own mailbox)
 * @param tableView the table view
 * @param sweepOptionType the sweep option type
 */
export default action('sweepConversations')(async function sweepConversations(
    sweepableMailboxes: EmailAddressWrapper[],
    tableView: TableView,
    sweepOptionType: SweepOptionType,
    destinationFolderId: string
): Promise<void> {
    const { keepNType, keepNCount } = getKeepNParameters(sweepOptionType);
    const sourceFolderId = tableView.tableQuery.folderId;

    const senderSMTPs = sweepableMailboxes.map(mailbox => mailbox.EmailAddress);
    const sweepCustomData: SweepBulkActionCustomData = {
        Scenario: 'Sweep',
        senderSMTPs: senderSMTPs,
        destinationFolderId: isFeatureEnabled('tri-moveToSweepOption')
            ? destinationFolderId
            : folderNameToId('deleteditems'),
    };

    if (isFeatureEnabled('tri-bulkActionNotification') && isFeatureEnabled('tri-sweepBulkAction')) {
        lazyOnBulkActionSubmitted.importAndExecute(
            tableView.tableQuery.folderId,
            'Sweep',
            sweepCustomData
        );
    }

    alwaysDeleteConversationsService(
        sweepableMailboxes,
        destinationFolderId,
        sourceFolderId,
        keepNType,
        keepNCount,
        sweepCustomData
    );

    if (sweepOptionType == SweepOptionType.All) {
        // No sweep rule needs to be created if sweep operation is to delete all emails from a sender.
        // In that case, just dismiss the dialog
        return;
    } else {
        // Show 'Creating...' dialog
        setSweepRequestStatus(SweepRequestStatus.Pending);
    }

    return createSweepRuleForSenderService(
        sweepableMailboxes,
        destinationFolderId,
        sourceFolderId,
        keepNType,
        keepNCount
    )
        .then(response => {
            if (response.ResponseMessages.Items[0].ResponseClass == 'Success') {
                // Show complete dialog if request succeeds
                setSweepRequestStatus(SweepRequestStatus.Complete);
            }
        })
        .catch(() => {
            // Show error dialog if request fails
            setSweepRequestStatus(SweepRequestStatus.Error);
        });
});
